import { Box, Tooltip, Typography } from "@mui/material";

import type { ConnectorEvent } from "@/types/connectors";

import { HexagonIcon } from "@/icons";

export interface TilesCountHexagonProps {
  count: number | undefined;
  events?: ConnectorEvent[];
  disabled?: boolean;
}

const CONTAINER_WIDTH = 30;
const CONTAINER_HEIGHT = 32;

const hexagonIconSx = {
  position: "absolute",
  width: CONTAINER_WIDTH,
  height: CONTAINER_HEIGHT,
} as const;

export function TilesCountHexagon({
  count,
  events,
  disabled = false,
}: TilesCountHexagonProps) {
  const enabledEvents = (events ?? []).filter((eventItem) => eventItem.enabled);
  const enabledEventsCount = enabledEvents.length;
  const displayCount = events ? enabledEventsCount : (count ?? 0);
  const hasTiles = enabledEventsCount > 0;
  const tooltipTitle = hasTiles ? (
    <Box component="span" sx={{ py: 2 }}>
      <Typography
        variant="subtitle2"
        component="div"
        sx={{ mb: 2, color: "inherit" }}
      >
        Connected Events:
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 3 }}>
        {enabledEvents.map((eventItem) => (
          <li key={eventItem.id}>
            <Typography variant="body2" component="span" color="inherit">
              {eventItem.name}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  ) : (
    <Typography variant="subtitle2" component="div" sx={{ color: "inherit" }}>
      No connected event
    </Typography>
  );

  const hexagon = (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: CONTAINER_WIDTH,
        height: CONTAINER_HEIGHT,
        flexShrink: 0,
      }}
    >
      <HexagonIcon
        weight="fill"
        sx={{
          ...hexagonIconSx,
          color: "common.white",
        }}
      />
      <HexagonIcon
        weight="light"
        sx={{
          ...hexagonIconSx,
          color: disabled ? "text.disabled" : "text.primary",
        }}
      />
      <Typography
        variant="body3"
        sx={{
          position: "relative",
          zIndex: 1,
          color: disabled ? "text.disabled" : "text.primary",
          textAlign: "center",
          fontWeight: 500,
          letterSpacing: "-0.1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {displayCount}
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      placement="bottom"
      arrow
      slotProps={{
        popper: {
          sx: (theme) => ({
            "& .MuiTooltip-tooltip": {
              backgroundColor: "grey.800",
              color: "common.white",
              padding: theme.spacing(3),
              px: theme.spacing(6),
              maxWidth: 280,
            },
            "& .MuiTooltip-arrow": {
              color: "grey.800",
            },
          }),
        },
      }}
    >
      <Box component="span" sx={{ display: "inline-flex" }}>
        {hexagon}
      </Box>
    </Tooltip>
  );
}
